import type { SorterResult } from "antd/es/table/interface";
import { useState } from "react";
import { getDataTableColumns } from "~components/DataTable/utils/getDataTableColumns.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import { useIntl } from "react-intl";
import { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";

type ColumnChooserConfig = {
  columnChooserLocalStorageKey: string;
  tableConfiguration: Array<DataTableColumnGroupDefinition>;
};

type ColumnSortConfig<T> = {
  sorterLocalStorageKey: string;
  defaultSorter?: SorterResult<T>;
};

type TableColumnsConfig<T> = {
  columnChooserConfig: ColumnChooserConfig;
  columnSortConfig: ColumnSortConfig<T>;
  tableColumns: DataTableColumn<T>[];
};

export const useTableColumns = <T>(config: TableColumnsConfig<T>) => {
  const { formatMessage } = useIntl();
  const initialSelectedColumns = getDataTableColumns(
    config.columnChooserConfig.columnChooserLocalStorageKey,
    config.columnChooserConfig.tableConfiguration,
  );

  const [selectedColumns, setSelectedColumns] = useState(initialSelectedColumns);
  const [sortConfig, setSortConfig] = useState<SorterResult<T> | SorterResult<T>[]>(() => {
    const saved = localStorage.getItem(config.columnSortConfig.sorterLocalStorageKey);
    if (saved) {
      return JSON.parse(saved);
    }
    return config.columnSortConfig.defaultSorter || {};
  });

  const setSortColumn = (sorter: SorterResult<T> | SorterResult<T>[]) => {
    setSortConfig(sorter);
    localStorage.setItem(config.columnSortConfig.sorterLocalStorageKey, JSON.stringify(sorter));
  };

  const plainColumnsConfig = (
    config.columnChooserConfig.tableConfiguration
      .flatMap((group) => group.columns)
      .filter((column) => column.key)
  );

  const allColumns = Object.fromEntries(config.tableColumns.map(column => ([column.key, column])))
  const persistentColumns = config.tableColumns.filter(x => x.persistent)

  const filteredByConfigColumns = plainColumnsConfig.map((column) => ({
    ...allColumns[column.key],
    title: column.titleIntlKey ? formatMessage({ id: column.titleIntlKey }) : allColumns[column.key]?.title,
  })).filter((x) => selectedColumns.includes(JSON.stringify(x.key))) as DataTableColumn<T>[];

  const mergedColumns = [...filteredByConfigColumns, ...persistentColumns]

  const columnsWithSorting = mergedColumns.map((column) => ({
    ...column,
    ...(Array.isArray(sortConfig)
      ? sortConfig.find((sort) => sort.columnKey === column.key)?.order && {
      defaultSortOrder: sortConfig.find((sort) => sort.columnKey === column.key)?.order,
    }
      : sortConfig?.columnKey === column.key && {
      defaultSortOrder: sortConfig?.order,
    })
  }));

  return { columnsToShow: columnsWithSorting, setSelectedColumns, setSortColumn };
};
